import { useEffect, useState } from 'react';

import Cookies from 'js-cookie';

import { LanguageCategory_Language } from '__generated__/protobuf/journey/data/categories/language_category';

import { Locale, LOCALES } from 'components/helpers/types';
import { getAnonymousId, setCookie } from 'components/shared/trackers';

import { determinePreferredLocale } from './getPreferredLocale';
import { i18n } from './i18n';
import { useObservability } from './observability';

export const useLanguagePreference = () => {
  const { trackJourneyEvent } = useObservability();
  const anonId = getAnonymousId();
  const localeCookie = Cookies.get('locale');
  const [defaultToSpanish, setDefaultToSpanish] = useState(localeCookie === LOCALES.SPANISH);
  const [loading, setLoading] = useState(!localeCookie);

  useEffect(() => {
    const loadLangPref = async () => {
      const preferredLocale = await determinePreferredLocale({
        localeFromCookie: localeCookie,
        anonId,
      });
      if (preferredLocale === LOCALES.SPANISH) {
        setDefaultToSpanish(true);
      }
      setLoading(false);
    };

    loadLangPref();
  }, [anonId, localeCookie]);

  useEffect(() => {
    if (localeCookie && i18n.language !== localeCookie) {
      i18n.changeLanguage(localeCookie);
    }
  }, [localeCookie]);

  // check for pending language toggle event and send it
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const toggledLanguage = window.localStorage.getItem('toggledLanguage');
    if (toggledLanguage) {
      const languageEvent = parseInt(toggledLanguage, 10) as LanguageCategory_Language;
      trackJourneyEvent('language', 'selectEvent', { language: languageEvent });
      window.localStorage.removeItem('toggledLanguage');
    }
  }, [trackJourneyEvent]);

  const toggleLanguage = () => {
    if (typeof window === 'undefined') return;
    const selectedLocale: Locale = defaultToSpanish ? LOCALES.ENGLISH : LOCALES.SPANISH;
    i18n.changeLanguage(selectedLocale);
    setCookie('locale', selectedLocale, 365);

    const languageEvent =
      selectedLocale === LOCALES.SPANISH
        ? LanguageCategory_Language.SPANISH
        : LanguageCategory_Language.ENGLISH;

    // store the language event in localStorage to send after reload
    window.localStorage.setItem('toggledLanguage', languageEvent.toString());

    // reload so we invoke routing to language-specific landing page in Cloudflare
    window.location.reload();
  };

  return { defaultToSpanish, loading, toggleLanguage };
};
